if (!customElements.get('product-modal')) {
  customElements.define('product-modal', class ProductModal extends ModalDialog {
    constructor() {
      super();
      this.classList.add('bv-gallery');
      window.addEventListener('resize', () => { if (this.hasAttribute('open')) this.layoutPhoto(); });
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse') event.stopPropagation();
      }, true);
      this.addEventListener('keydown', (event) => {
        if (!this.hasAttribute('open')) return;
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          this.navigate(event.key === 'ArrowRight' ? 1 : -1);
        }
      });
      this.addEventListener('touchstart', (event) => {
        this.touchStart = event.touches.length === 1 && !this.classList.contains('bv-gallery--zoomed')
          ? { x: event.touches[0].clientX, y: event.touches[0].clientY } : null;
      }, { passive: true });
      this.addEventListener('touchmove', (event) => {
        if (event.touches.length !== 1) this.touchStart = null;
      }, { passive: true });
      this.addEventListener('touchend', (event) => {
        if (!this.touchStart || !event.changedTouches.length) return;
        const dx = event.changedTouches[0].clientX - this.touchStart.x;
        const dy = event.changedTouches[0].clientY - this.touchStart.y;
        this.touchStart = null;
        if (Math.abs(dx) > 55 && Math.abs(dx) > Math.abs(dy) * 1.5 &&
          !event.target.closest('button, video, iframe, model-viewer')) this.navigate(dx < 0 ? 1 : -1);
      }, { passive: true });
    }
    setupGallery() {
      if (this.galleryReady) return;
      this.galleryReady = true;
      this.container = this.querySelector('[role="document"]');
      this.items = Array.from(this.container.children).filter((item) => item.hasAttribute('data-media-id'));
      const english = document.documentElement.lang.startsWith('en');
      const controls = document.createElement('div');
      controls.className = 'bv-gallery__controls';
      const button = (label, text, className, action) => {
        const element = document.createElement('button');
        element.type = 'button';
        element.className = className;
        element.setAttribute('aria-label', label);
        element.textContent = text;
        element.addEventListener('click', action);
        return element;
      };
      this.previousButton = button(english ? 'Previous photo' : 'Попереднє фото', '‹', 'bv-gallery__prev', () => this.navigate(-1));
      this.nextButton = button(english ? 'Next photo' : 'Наступне фото', '›', 'bv-gallery__next', () => this.navigate(1));
      this.counter = document.createElement('span');
      this.counter.className = 'bv-gallery__counter';
      this.counter.setAttribute('role', 'status');
      this.counter.setAttribute('aria-live', 'polite');
      this.zoomButton = button(english ? 'Zoom photo' : 'Збільшити фото', '+', 'bv-gallery__zoom', () => {
        this.setZoom(!this.classList.contains('bv-gallery--zoomed'));
      });
      this.zoomButton.setAttribute('aria-pressed', 'false');
      controls.append(this.previousButton, this.counter, this.nextButton, this.zoomButton);
      this.querySelector('[role="dialog"]').appendChild(controls);
    }
    show(opener) {
      this.setupGallery();
      super.show(opener);
      const id = opener.getAttribute('data-media-id');
      const index = this.items.findIndex((item) => item.getAttribute('data-media-id') === id);
      this.activate(index < 0 ? 0 : index);
    }
    navigate(direction) {
      if (this.items.length < 2) return;
      this.activate((this.currentIndex + direction + this.items.length) % this.items.length);
    }
    setZoom(zoomed) {
      this.classList.toggle('bv-gallery--zoomed', Boolean(zoomed));
      this.zoomButton.textContent = zoomed ? '−' : '+';
      this.zoomButton.setAttribute('aria-pressed', String(Boolean(zoomed)));
      const english = document.documentElement.lang.startsWith('en');
      this.zoomButton.setAttribute('aria-label', english ? (zoomed ? 'Fit photo' : 'Zoom photo') : (zoomed ? 'Показати фото повністю' : 'Збільшити фото'));
      this.layoutPhoto();
    }
    layoutPhoto() {
      const active = this.items[this.currentIndex];
      if (!active || active.tagName !== 'IMG') return;
      requestAnimationFrame(() => {
        if (!active.classList.contains('active')) return;
        const style = getComputedStyle(this.container);
        const width = this.container.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
        const height = this.container.clientHeight - parseFloat(style.paddingTop) - parseFloat(style.paddingBottom);
        const naturalWidth = active.naturalWidth || Number(active.getAttribute('width'));
        const naturalHeight = active.naturalHeight || Number(active.getAttribute('height'));
        if (!naturalWidth || !naturalHeight) return;
        const fit = Math.min(width / naturalWidth, height / naturalHeight);
        const zoomed = this.classList.contains('bv-gallery--zoomed');
        const rootSize = parseFloat(getComputedStyle(document.documentElement).fontSize);
        const productionWidth = window.matchMedia('(min-width: 750px)').matches
          ? this.container.clientWidth - 22 * rootSize
          : Number(active.getAttribute('width')) || 1100;
        const scale = zoomed ? productionWidth / naturalWidth : fit;
        active.style.width = Math.round(naturalWidth * scale) + 'px';
        active.style.height = Math.round(naturalHeight * scale) + 'px';
        requestAnimationFrame(() => {
          if (!active.classList.contains('active')) return;
          this.container.scrollLeft = Math.max(0, (this.container.scrollWidth - this.container.clientWidth) / 2);
          this.container.scrollTop = 0;
        });
      });
    }

    activate(index) {
      window.pauseAllMedia();
      this.currentIndex = index;
      this.items.forEach((item, i) => {
        item.classList.toggle('active', i === index);
        item.setAttribute('aria-hidden', String(i !== index));
      });
      const active = this.items[index];
      this.counter.textContent = (index + 1) + ' / ' + this.items.length;
      this.previousButton.disabled = this.nextButton.disabled = this.items.length < 2;
      this.zoomButton.hidden = !active || active.tagName !== 'IMG';
      this.container.scrollTop = 0;
      this.container.scrollLeft = 0;
      if (active && active.tagName === 'IMG') {
        active.loading = 'eager';
        active.addEventListener('load', () => { if (active.classList.contains('active')) this.layoutPhoto(); }, { once: true });
      }
      this.setZoom(active && active.tagName === 'IMG');
      const template = active && active.querySelector('template');
      if (active && active.nodeName === 'DEFERRED-MEDIA' && template && template.content.querySelector('.js-youtube')) active.loadContent();
    }
  });
}
